import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { FileText, Download, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoicesPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const res = await axios.get(`${API}/invoices`, { headers: { Authorization: `Bearer ${token}` } });
        setInvoices(res.data.invoices || []);
      } catch { }
      setLoading(false);
    };
    fetchInvoices();
  }, [token]);

  const downloadPdf = async (invoiceId, invoiceNum) => {
    setDownloading(invoiceId);
    try {
      const res = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${invoiceNum || invoiceId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Error al descargar la factura');
    }
    setDownloading(null);
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts * 1000);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const hasFiscalData = user?.fiscal_data?.fiscal_name && user?.fiscal_data?.cif;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="invoices-page">
        <h1 className="font-heading text-3xl font-bold text-white mb-2">FACTURAS</h1>
        <p className="text-zinc-400 mb-8">Historial de facturas de tus compras</p>

        {!hasFiscalData && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-4 mb-6 flex items-start gap-3" data-testid="no-fiscal-warning">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 text-sm font-medium">Datos fiscales incompletos</p>
              <p className="text-amber-400/70 text-xs mt-1">Completa tus datos fiscales para que se generen facturas con tus compras.</p>
              <button onClick={() => navigate('/profile')} className="text-primary text-xs mt-2 hover:underline font-medium">Ir a Mi Perfil</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="bg-surface border border-white/10 rounded-md p-12 text-center">
            <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No tienes facturas todavia</p>
            <p className="text-zinc-600 text-sm mt-1">Las facturas se generan automaticamente al comprar creditos</p>
          </div>
        ) : (
          <div className="bg-surface border border-white/10 rounded-md overflow-hidden">
            <table className="w-full" data-testid="invoices-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-zinc-500 text-xs uppercase tracking-wider px-4 py-3">N Factura</th>
                  <th className="text-left text-zinc-500 text-xs uppercase tracking-wider px-4 py-3">Fecha</th>
                  <th className="text-right text-zinc-500 text-xs uppercase tracking-wider px-4 py-3">Total</th>
                  <th className="text-center text-zinc-500 text-xs uppercase tracking-wider px-4 py-3">Estado</th>
                  <th className="text-right text-zinc-500 text-xs uppercase tracking-wider px-4 py-3">PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-white text-sm font-mono">{inv.number || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-zinc-400 text-sm">{formatDate(inv.date)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-white text-sm font-mono font-medium">{inv.total?.toFixed(2)}€</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded-full ${inv.status === 'paid' ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
                        {inv.status === 'paid' ? 'Pagada' : 'Pendiente'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadPdf(inv.id, inv.number)}
                        disabled={downloading === inv.id}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`download-invoice-${inv.id}`}
                      >
                        {downloading === inv.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InvoicesPage;
